import React, { useState } from "react";

import { PlayArrowRounded } from "@mui/icons-material";
import { Box, ButtonBase, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

export interface TrainingVideoProps {
  title: string;
  loomId: string;
  thumbnailUrl?: string;
}

// Loom needs its own origin and scripts. The frame remains cross-origin and
// cannot navigate the portal's top-level page.
const LOOM_IFRAME_SANDBOX =
  "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox";

const TrainingVideo: React.FC<TrainingVideoProps> = ({
  title,
  loomId,
  thumbnailUrl,
}) => {
  const { t } = useTranslation("training");
  const [hasRequestedPlayback, setHasRequestedPlayback] = useState(false);
  const [isFrameLoaded, setIsFrameLoaded] = useState(false);

  return (
    <Box
      sx={(theme) => ({
        aspectRatio: "16 / 9",
        backgroundColor: "action.hover",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        minHeight: theme.spacing(16),
        overflow: "hidden",
        position: "relative",
        width: "100%",
      })}
    >
      {hasRequestedPlayback && (
        <Box
          component="iframe"
          src={`https://www.loom.com/embed/${loomId}?autoplay=1`}
          title={title}
          referrerPolicy="no-referrer"
          sandbox={LOOM_IFRAME_SANDBOX}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          onLoad={() => setIsFrameLoaded(true)}
          sx={{
            border: 0,
            height: "100%",
            position: "absolute",
            visibility: isFrameLoaded ? "visible" : "hidden",
            width: "100%",
          }}
        />
      )}
      {!isFrameLoaded && (
        <ButtonBase
          aria-label={`${t("playVideo")}: ${title}`}
          disabled={hasRequestedPlayback}
          onClick={() => setHasRequestedPlayback(true)}
          sx={(theme) => ({
            alignItems: "center",
            backgroundImage: thumbnailUrl
              ? `url("${thumbnailUrl}")`
              : undefined,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            display: "grid",
            gridTemplateColumns: "1fr",
            gridTemplateRows: "1fr auto",
            height: "100%",
            inset: 0,
            justifyItems: "center",
            position: "absolute",
            width: "100%",
            "&:focus-visible": {
              outline: `${theme.spacing(0.25)} solid ${theme.palette.primary.main}`,
              outlineOffset: theme.spacing(-0.5),
            },
          })}
        >
          <Box
            component="span"
            sx={(theme) => ({
              alignItems: "center",
              backgroundColor: "background.paper",
              borderRadius: "50%",
              boxShadow: 3,
              color: "primary.main",
              display: "flex",
              height: theme.spacing(8),
              justifyContent: "center",
              width: theme.spacing(8),
            })}
          >
            {hasRequestedPlayback ? (
              <CircularProgress aria-hidden="true" />
            ) : (
              <PlayArrowRounded fontSize="large" />
            )}
          </Box>
          <Typography
            component="span"
            role="status"
            sx={{
              backgroundColor: "background.paper",
              color: "text.primary",
              fontWeight: 600,
              p: 1,
              width: "100%",
            }}
          >
            {t(hasRequestedPlayback ? "loadingVideo" : "playVideo")}
          </Typography>
        </ButtonBase>
      )}
    </Box>
  );
};

export default TrainingVideo;
